import { SectionKey } from "../sections"

export interface RouteInfo {
  sectionName: SectionKey
  subRoute: string
  fullPath: string
}

/**
 * Parses a browser URL path to extract section and sub-route information
 * @param pathname - The browser pathname (e.g., "/orders/calendar")
 * @returns RouteInfo object or null if not a valid section route
 */
export function parseRouteFromPath(pathname: string): RouteInfo | null {
  // Remove leading slash if present
  const cleanPath = pathname.startsWith("/") ? pathname.slice(1) : pathname

  // Handle root path
  if (!cleanPath || cleanPath === "/") {
    return null
  }

  const segments = cleanPath.split("/")
  const sectionName = segments[0] as SectionKey

  // Validate section name
  const validSections: SectionKey[] = [
    "dashboards",
    "assets",
    "customers",
    "systems",
    "projects",
    "planner",
    "connections",
    "orders",
    "filling",
    "alerts",
  ]
  if (!validSections.includes(sectionName)) {
    return null
  }

  // Extract sub-route (everything after section name)
  const subRoute = segments.length > 1 ? "/" + segments.slice(1).join("/") : "/"

  return {
    sectionName,
    subRoute,
    fullPath: pathname,
  }
}

/**
 * Constructs a full browser path from section name and sub-route
 * @param sectionName - The section name
 * @param subRoute - The sub-route within the section (should start with /)
 * @returns Full path for browser navigation
 */
export function constructSectionPath(sectionName: SectionKey, subRoute: string): string {
  // Ensure subRoute starts with /
  const normalizedSubRoute = subRoute.startsWith("/") ? subRoute : "/" + subRoute

  // Special handling for root route with query params: "/?..." should become "?..."
  if (normalizedSubRoute.startsWith("/?")) {
    return `/${sectionName}${normalizedSubRoute.slice(1)}`
  }

  // Remove trailing slash unless it's the root
  const cleanSubRoute = normalizedSubRoute === "/" ? "" : normalizedSubRoute.replace(/\/$/, "")

  return `/${sectionName}${cleanSubRoute}`
}

/**
 * Validates and normalizes a sub-route
 * @param subRoute - The sub-route to validate
 * @returns Normalized sub-route (always starts with /)
 */
export function normalizeSubRoute(subRoute: string): string {
  // Handle null, undefined, or empty string
  if (!subRoute || subRoute === "" || subRoute.trim() === "") {
    return "/"
  }

  // Trim whitespace
  let normalized = subRoute.trim()

  // Ensure it starts with / - this is critical for MemoryRouter
  // MemoryRouter requires absolute paths, not relative paths
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`
  }

  // Remove trailing slash unless it's the root
  if (normalized !== "/" && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1)
  }

  // Final validation: ensure we always return an absolute path
  // This is a defensive check to prevent any edge cases from returning relative paths
  if (!normalized.startsWith("/")) {
    console.warn("[normalizeSubRoute] Path normalization failed, forcing absolute path:", normalized)
    normalized = `/${normalized}`
  }

  return normalized
}

/**
 * Extracts the sub-route from a full section path
 * @param fullPath - The full path (e.g., "/orders/calendar")
 * @param sectionName - The section name to extract from
 * @returns The sub-route portion (always normalized with leading slash)
 */
export function extractSubRoute(fullPath: string, sectionName: SectionKey): string {
  // Validate inputs
  if (!fullPath || typeof fullPath !== "string") {
    console.warn("[extractSubRoute] Invalid fullPath provided:", fullPath)
    return "/"
  }

  if (!sectionName || typeof sectionName !== "string") {
    console.warn("[extractSubRoute] Invalid sectionName provided:", sectionName)
    return "/"
  }

  // Ensure fullPath is absolute (starts with /)
  // This handles edge cases where a relative path might be passed
  const normalizedFullPath = fullPath.startsWith("/") ? fullPath : `/${fullPath}`

  const sectionPrefix = `/${sectionName}`

  // If the path doesn't start with the section prefix, return root
  if (!normalizedFullPath.startsWith(sectionPrefix)) {
    return "/"
  }

  // Extract the sub-route by removing the section prefix
  const subRoute = normalizedFullPath.slice(sectionPrefix.length)

  // Normalize the sub-route (ensures leading slash and proper formatting)
  const normalized = normalizeSubRoute(subRoute || "/")

  // Final defensive check: ensure we always return an absolute path
  // This guarantees MemoryRouter compatibility
  if (!normalized.startsWith("/")) {
    console.error("[extractSubRoute] normalizeSubRoute returned relative path, fixing:", normalized)
    return `/${normalized}`
  }

  return normalized
}

/**
 * Extracts the sub-route from a pathname and search query string for a specific section,
 * stripping hub-level query parameters (like preview) while preserving section parameters (like tab).
 * @param pathname - The browser pathname (e.g., "/assets/123/settings")
 * @param search - The browser search string (e.g., "?tab=alert-configuration&preview=hash")
 * @param sectionName - The section name (e.g., "assets")
 * @returns The sub-route with preserved section query params (e.g., "/123/settings?tab=alert-configuration")
 */
export function extractSectionSubRoute(pathname: string, search: string, sectionName: SectionKey): string {
  const subRoutePath = extractSubRoute(pathname, sectionName)
  if (!search) {
    return subRoutePath
  }

  // Parse and strip hub-level parameters
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
  params.delete("preview")

  const remainingSearch = params.toString()
  if (!remainingSearch) {
    return subRoutePath
  }

  return `${subRoutePath}${subRoutePath.includes("?") ? "&" : "?"}${remainingSearch}`
}

/**
 * Checks if a path belongs to a specific section
 * @param pathname - The path to check
 * @param sectionName - The section name
 * @returns True if the path belongs to the section
 */
export function isPathForSection(pathname: string, sectionName: SectionKey): boolean {
  const routeInfo = parseRouteFromPath(pathname)
  return routeInfo?.sectionName === sectionName
}

/**
 * Checks if a full path is a global settings route that should be handled by the hub
 * @param pathname - The full pathname to check
 * @returns True if it's a global settings route
 */
export function isGlobalSettingsRoute(pathname: string): boolean {
  const globalSettingsRoutes = ["/settings/password", "/settings/admin", "/settings/organization", "/whats-new"]

  return globalSettingsRoutes.includes(pathname)
}

/**
 * Route change callback type for sections to notify hub of navigation
 */
export type RouteChangeCallback = (newSubRoute: string) => void

/**
 * Checks if a section is currently at its home route
 * @param pathname - The current pathname
 * @param sectionName - The section name to check
 * @returns True if the section is at its home route
 */
export function isSectionHome(pathname: string, sectionName: SectionKey): boolean {
  const routeInfo = parseRouteFromPath(pathname)
  if (!routeInfo || routeInfo.sectionName !== sectionName) {
    return false
  }
  return routeInfo.subRoute === "/"
}

/**
 * Gets the home path for a specific section
 * @param sectionName - The section name
 * @returns The home path for the section (e.g., "/orders")
 */
export function getSectionHome(sectionName: SectionKey): string {
  return `/${sectionName}`
}

/**
 * Creates a route change handler for a specific section
 * @param sectionName - The section name
 * @param onRouteChange - Callback to handle route changes
 * @returns Route change handler function
 */
export function createRouteChangeHandler(
  sectionName: SectionKey,
  onRouteChange: (fullPath: string) => void,
): RouteChangeCallback {
  return (newSubRoute: string) => {
    // Handle case where newSubRoute is already a full path (includes section name)
    // This can happen when RouteChangeNotifier passes the full MemoryRouter pathname
    // Example: "/assets/04e49159-..." instead of just "/04e49159-..."
    let subRoute: string

    // Check if the path already starts with the section name
    const sectionPrefix = `/${sectionName}`
    if (newSubRoute.startsWith(sectionPrefix)) {
      // Extract the sub-route from the full path
      subRoute = extractSubRoute(newSubRoute, sectionName)
    } else {
      // It's already a sub-route, just normalize it
      subRoute = normalizeSubRoute(newSubRoute)
    }

    // Construct the full path from the section name and sub-route
    const fullPath = constructSectionPath(sectionName, subRoute)

    onRouteChange(fullPath)
  }
}
